import { PlanType } from "@optidev/shared-types";
import { PricingResponse } from "../../../lib/api/billing";

// Static plan info (features, descriptions). Single source of truth shared by the Billing
// page and the publish-time upsell modal so both render identical plans.
export const PLAN_INFO: Record<
  string,
  {
    name: string;
    description: string;
    features: string[];
    featureHeader?: string;
    ctaText?: string;
    monthlyCredits?: number;
  }
> = {
  [PlanType.STANDARD]: {
    name: "Standard",
    description: "For individuals and small teams",
    monthlyCredits: 100,
    featureHeader: "Everything in Free, plus:",
    features: [
      "Claude Haiku & Sonnet + GPT, Gemini, Qwen, MiMo",
      "Public, private & internal apps",
      "Custom domain support",
      "$10 Cloud Balance per month",
      "3 Database Compute included",
    ],
  },
  [PlanType.PRO]: {
    name: "Pro",
    description: "For growing teams",
    monthlyCredits: 200,
    featureHeader: "Everything in Standard, plus:",
    features: [
      "All Standard models + Claude 4.7 Opus",
      "SSO/SAML authentication",
      "$15 Cloud Balance per month",
      "6 Database Compute included",
    ],
  },
  [PlanType.ENTERPRISE]: {
    name: "Enterprise",
    description: "For large organizations",
    featureHeader: "Everything in Pro, plus:",
    features: [
      "Dedicated support team",
      "Onboarding services",
      "Forward deploy engineering",
      "Custom integrations & connections",
      "Tailored Cloud Balance & Database Compute",
    ],
    ctaText: "Book a demo",
  },
};

// Currency symbols for formatting
export const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: "$",
  gbp: "£",
  eur: "€",
  cad: "C$",
  aud: "A$",
  jpy: "¥",
};

export interface PlanPrices {
  monthlyPrice: number | string;
  yearlyPrice: number | string;
}

/**
 * Resolve the monthly/yearly display prices (in major units) for a plan from the public
 * pricing response. Enterprise has no fixed price ("Custom"); Standard/Pro convert from cents
 * and fall back to sensible defaults when the API omits the selected currency.
 */
export function getPlanPrices(
  pricing: PricingResponse | null,
  planType: PlanType,
  currency: string,
): PlanPrices {
  if (planType === PlanType.STANDARD && pricing?.plans?.standard) {
    const monthlyAmount = pricing.plans.standard.monthly[currency];
    const yearlyAmount = pricing.plans.standard.yearly[currency];
    return {
      monthlyPrice: monthlyAmount ? monthlyAmount / 100 : 25,
      yearlyPrice: yearlyAmount ? yearlyAmount / 100 : 300,
    };
  }

  if (planType === PlanType.PRO && pricing?.plans?.pro) {
    const monthlyAmount = pricing.plans.pro.monthly[currency];
    const yearlyAmount = pricing.plans.pro.yearly[currency];
    return {
      monthlyPrice: monthlyAmount ? monthlyAmount / 100 : 50,
      yearlyPrice: yearlyAmount ? yearlyAmount / 100 : 600,
    };
  }

  return { monthlyPrice: "Custom", yearlyPrice: "Custom" };
}
